import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { useControllableState } from '../lib/hooks';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedOption<T>[];
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  /** Accessible group label, e.g. "View" or "Date range". */
  'aria-label': string;
  size?: 'sm' | 'md';
  className?: string;
}

/** Segmented control — the Calendar/List toggle and date-range presets (§7.5/§7.6). */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  defaultValue,
  onChange,
  size = 'md',
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const [selected, setSelected] = useControllableState<T>(
    value,
    defaultValue ?? options[0]!.value,
    onChange,
  );
  return (
    <div
      role="group"
      aria-label={rest['aria-label']}
      className={cx('tc-segmented', size === 'sm' && 'tc-segmented--sm', className)}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="tc-segmented__item"
          aria-pressed={selected === o.value}
          disabled={o.disabled}
          onClick={() => setSelected(o.value)}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}
